import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Container, Spinner, Table } from "react-bootstrap";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import PageLayout from "../../../layouts/PageLayout";

const PAGE_TITLES = {
  sales: "All Sales",
  appointments: "All Appointments",
  billed: "Total Bill",
  paid: "Money Received",
  unpaid: "Unpaid"
};

// Same colour per card as on the dashboard
const PAGE_TONES = {
  sales: "is-blue",
  appointments: "is-purple",
  billed: "is-slate",
  paid: "is-green",
  unpaid: "is-red"
};

// Shown next to appointments booked before today
const PreviousBadge = () => (
  <span className="dd-badge">Previous</span>
);

const formatAmount = (value) =>
  parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const formatDate = (value) => {
  if (!value) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
};

const sumField = (rows, field) =>
  rows.reduce((total, row) => total + (parseFloat(row[field]) || 0), 0);

const EyeButton = ({ onClick, title }) => (
  <button type="button" className="dd-view-btn" onClick={onClick} title={title} aria-label={title}>
    <FaEye />
  </button>
);

export default function DashboardDetailsPage() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchParams.get("start_date")) params.append("start_date", searchParams.get("start_date"));
        if (searchParams.get("end_date")) params.append("end_date", searchParams.get("end_date"));

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/details?${params.toString()}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
        );

        if (response.data.success) {
          setData(response.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [searchParams]);

  const sales = data?.sales || [];
  const appointments = data?.appointments || [];
  const payments = data?.payments || [];
  const outstanding = (rows) => rows.filter((row) => row.unpaid > 0.005);

  const sections = (() => {
    switch (type) {
      case "sales":
        return [{ title: "Sales", kind: "billing", entity: "sale", rows: sales }];
      case "appointments":
        return [{ title: "Appointments", kind: "billing", entity: "appointment", rows: appointments }];
      case "billed":
        return [
          { title: "Sales", kind: "billing", entity: "sale", rows: sales },
          { title: "Appointments", kind: "billing", entity: "appointment", rows: appointments }
        ];
      case "paid":
        return [{ title: "Payments Received", kind: "payments", rows: payments }];
      case "unpaid":
        // Only records that still have a balance
        return [
          { title: "Unpaid Sales", kind: "billing", entity: "sale", rows: outstanding(sales) },
          { title: "Unpaid Appointments", kind: "billing", entity: "appointment", rows: outstanding(appointments) }
        ];
      default:
        return [];
    }
  })();

  const billingRows = sections
    .filter((section) => section.kind === "billing")
    .flatMap((section) => section.rows);

  const openSale = (row) => navigate(`/sale-edit/${row.id}`);
  const openAppointment = (row) => navigate(`/appointments/${row.id}`);

  const renderSummary = () => {
    if (type === "paid") {
      return (
        <SummaryCards
          items={[{ label: "Total Received", value: sumField(payments, "amount"), tone: "is-green" }]}
        />
      );
    }

    if (type === "unpaid") {
      return (
        <SummaryCards
          items={[{ label: "Total Unpaid", value: sumField(billingRows, "unpaid"), tone: "is-red" }]}
        />
      );
    }

    if (type === "sales" || type === "appointments" || type === "billed") {
      return (
        <SummaryCards
          items={[
            { label: "Total Bill", value: sumField(billingRows, "total"), tone: "is-slate" },
            { label: "Received", value: sumField(billingRows, "paid"), tone: "is-green" },
            { label: "Unpaid", value: sumField(billingRows, "unpaid"), tone: "is-red" }
          ]}
        />
      );
    }

    return null;
  };

  const renderBillingTable = (section) => (
    <section className="dd-section" key={section.title}>
      <div className="dd-section-head">
        <h5 className="dd-section-title">{section.title}</h5>
        <span className="dd-count">{section.rows.length}</span>
      </div>
      {section.rows.length === 0 ? (
        <div className="dd-empty">No records found</div>
      ) : (
        <Table responsive hover className="dd-table mb-0 align-middle">
          <thead>
            <tr>
              <th>No.</th>
              <th>Date</th>
              <th>Customer</th>
              <th className="text-end">Total</th>
              <th className="text-end">Received</th>
              <th className="text-end">Unpaid</th>
              <th className="text-center">View</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => {
              const isSale = section.entity === "sale";

              return (
                <tr key={`${section.title}-${row.id}`}>
                  <td>
                    <strong className="dd-ref">{row.number}</strong>
                    {!isSale && row.is_previous && <PreviousBadge />}
                  </td>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.customer || "-"}</td>
                  <td className="text-end dd-num">{formatAmount(row.total)}</td>
                  <td className="text-end dd-num is-green">{formatAmount(row.paid)}</td>
                  <td className="text-end">
                    <span className={`dd-amount-chip ${row.unpaid > 0.005 ? "is-red" : "is-clear"}`}>
                      {formatAmount(row.unpaid)}
                    </span>
                  </td>
                  <td className="text-center">
                    <EyeButton
                      title={isSale ? "View sale" : "View appointment"}
                      onClick={() => (isSale ? openSale(row) : openAppointment(row))}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="3" className="text-end">Total</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "total"))}</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "paid"))}</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "unpaid"))}</th>
              <th></th>
            </tr>
          </tfoot>
        </Table>
      )}
    </section>
  );

  const renderPaymentsTable = (section) => (
    <section className="dd-section" key={section.title}>
      <div className="dd-section-head">
        <h5 className="dd-section-title">{section.title}</h5>
        <span className="dd-count">{section.rows.length}</span>
      </div>
      {section.rows.length === 0 ? (
        <div className="dd-empty">No records found</div>
      ) : (
        <Table responsive hover className="dd-table mb-0 align-middle">
          <thead>
            <tr>
              <th>Date</th>
              <th>For</th>
              <th>No.</th>
              <th>Customer</th>
              <th>Paid With</th>
              <th className="text-end">Amount</th>
              <th className="text-center">View</th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, idx) => (
              <tr key={`${row.type}-${row.id}-${idx}`}>
                <td>{formatDate(row.date)}</td>
                <td>
                  <span className={`dd-type ${row.type === "Sale" ? "is-blue" : "is-purple"}`}>
                    {row.type === "Sale" ? "Sale" : "Appointment"}
                  </span>
                  {row.type !== "Sale" && row.is_previous && <PreviousBadge />}
                </td>
                <td><strong className="dd-ref">{row.reference}</strong></td>
                <td>{row.customer || "-"}</td>
                <td>{row.method || "-"}</td>
                <td className={`text-end dd-num fw-bold ${row.amount < 0 ? "is-red" : "is-green"}`}>
                  {formatAmount(row.amount)}
                </td>
                <td className="text-center">
                  <EyeButton
                    title={row.type === "Sale" ? "View sale" : "View appointment"}
                    onClick={() => (row.type === "Sale" ? openSale(row) : openAppointment(row))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="5" className="text-end">Total Received</th>
              <th className="text-end">{formatAmount(sumField(section.rows, "amount"))}</th>
              <th></th>
            </tr>
          </tfoot>
        </Table>
      )}
    </section>
  );

  return (
    <PageLayout>
      <Container fluid className="px-0">
        <div className={`dd-page ${PAGE_TONES[type] || "is-blue"}`}>
          <div className="dd-header">
            <button type="button" className="dd-back" onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back
            </button>
            <h3 className="dd-title">{PAGE_TITLES[type] || "Details"}</h3>
            {(searchParams.get("start_date") || searchParams.get("end_date")) && (
              <span className="dd-range">
                {formatDate(searchParams.get("start_date"))} – {formatDate(searchParams.get("end_date"))}
              </span>
            )}
          </div>

          {loading ? (
            <div className="dd-loading">
              <Spinner animation="border" variant="primary" />
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {renderSummary()}

              {sections.map((section) =>
                section.kind === "payments"
                  ? renderPaymentsTable(section)
                  : renderBillingTable(section)
              )}
            </>
          )}
        </div>
      </Container>
    </PageLayout>
  );
}

function SummaryCards({ items }) {
  return (
    <div className="dd-summary">
      {items.map((item) => (
        <div className={`dd-stat ${item.tone}`} key={item.label}>
          <span className="dd-stat-label">{item.label}</span>
          <span className="dd-stat-value">{formatAmount(item.value)}</span>
        </div>
      ))}
    </div>
  );
}
